/**
 * Calendar-day arithmetic for the period resolver. A day is `YYYY-MM-DD`; a
 * `Date` is read by its UTC components, which is how a date-only control date
 * is stored (`new Date('YYYY-MM-DD')` is UTC midnight). No time zone is ever
 * applied here, so the same input resolves to the same day on every machine.
 */

export interface CalendarDay {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
}

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Read a calendar day from a `YYYY-MM-DD` string (a longer ISO string is
 * truncated to its date part) or from a Date's UTC components.
 *
 * @throws Error when the string does not start with a date.
 */
export function toCalendarDay(value: string | Date): CalendarDay {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) throw new Error('Invalid date');
    return {
      year: value.getUTCFullYear(),
      month: value.getUTCMonth() + 1,
      day: value.getUTCDate(),
    };
  }
  const match = DAY_PATTERN.exec(value);
  if (!match) throw new Error(`Not a calendar day: ${value}`);
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

export function formatCalendarDay(day: CalendarDay): string {
  const mm = String(day.month).padStart(2, '0');
  const dd = String(day.day).padStart(2, '0');
  return `${day.year}-${mm}-${dd}`;
}

/** Number of days in a month (1-12); the month may be out of range and is normalised. */
function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Step a day back `months` calendar months, clamping the day to the target
 * month's length so 31 May − 3 months is 28 Feb, not 3 March.
 */
export function subtractMonths(day: CalendarDay, months: number): CalendarDay {
  const anchor = new Date(Date.UTC(day.year, day.month - 1 - months, 1));
  const year = anchor.getUTCFullYear();
  const month = anchor.getUTCMonth() + 1;
  return { year, month, day: Math.min(day.day, daysInMonth(year, month)) };
}
